import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import TopNavigation from '../components/Navigation/TopNavigation'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import PageContent from '../components/Page/PageContent'
import DashboardGrid from '../components/Dashboard/DashboardGrid'
import DashboardMobileList from '../components/Dashboard/DashboardMobileList'
import DashboardEditActions from '../components/Dashboard/DashboardEditActions'
import AddWidgetList from '../components/Dashboard/AddWidgetList'
import {
  getDefaultLayout,
  getEffectiveLayout,
  WIDGET_REGISTRY,
  WidgetProps,
  WidgetType,
} from '../components/Dashboard/widgetRegistry'
import {
  appendItem,
  autoLayout,
  DASHBOARD_LAYOUT_VERSION,
  generateItemId,
  moveItem,
} from '../components/Dashboard/layout/layoutUtils'
import DashboardController from '../controllers/DashboardController'
import { useIsMobile } from '../hooks/use-mobile'
import { BREAKPOINT_SMALL_VALUE } from '../styles/breakpoints'
import { AppState } from '../store'
import { setCurrentUser } from '../store/authentication/actions'
import Notification from '../utilities/Notification'
import { DashboardLayoutItem } from '../types'

const Dashboard = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch() as Dispatch<AnyAction>
  const currentUser = useSelector((state: AppState) => state.authentication.currentUser)
  const isMobile = useIsMobile(BREAKPOINT_SMALL_VALUE)

  const savedLayout = currentUser.user_workspace_setting?.dashboard_layout
  const savedItems = React.useMemo(() => getEffectiveLayout(savedLayout), [savedLayout])

  // Edit state: `draft` holds the working layout until Save; `resetToDefault`
  // makes Save send `null` (use the default) until the user edits again.
  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState<DashboardLayoutItem[] | null>(null)
  const [resetToDefault, setResetToDefault] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const items = editing && draft ? draft : savedItems

  const { default_currency, number_format } = currentUser.workspace.setting
  const widgetProps = React.useMemo<WidgetProps>(
    () => ({ currency: default_currency, numberFormat: number_format }),
    [default_currency, number_format]
  )

  const onCustomise = React.useCallback(() => {
    setDraft(structuredClone(savedItems))
    // No saved layout means the user is on the default: saving untouched keeps it `null`.
    setResetToDefault(!savedLayout)
    setEditing(true)
  }, [savedItems, savedLayout])

  const onCancel = React.useCallback(() => {
    setEditing(false)
    setDraft(null)
    setResetToDefault(false)
  }, [])

  // The grid's compactor can report changes on its own (e.g. a saved layout with
  // gaps), so only accept layout changes while editing.
  const onLayoutChange = React.useCallback((nextItems: DashboardLayoutItem[]) => {
    if (!editing) return
    setDraft(nextItems)
    setResetToDefault(false)
  }, [editing])

  const onRemoveWidget = React.useCallback((i: string) => {
    if (!editing) return
    setDraft((current) => (current ?? []).filter((item) => item.i !== i))
    setResetToDefault(false)
  }, [editing])

  const onAddWidget = React.useCallback((type: WidgetType) => {
    setDraft((current) => appendItem(current ?? [], { i: generateItemId(), type, ...WIDGET_REGISTRY[type].defaultSize }))
    setResetToDefault(false)
  }, [])

  const onMoveWidget = React.useCallback((i: string, direction: -1 | 1) => {
    setDraft((current) => moveItem(current ?? [], i, direction))
    setResetToDefault(false)
  }, [])

  const onAutoLayout = React.useCallback(() => {
    setDraft((current) => autoLayout(current ?? []))
    setResetToDefault(false)
  }, [])

  const onReset = React.useCallback(() => {
    setDraft(getDefaultLayout())
    setResetToDefault(true)
  }, [])

  const onSave = React.useCallback(async () => {
    if (!draft || saving) return
    setSaving(true)
    try {
      const response = await DashboardController.updateLayout(
        resetToDefault ? null : { version: DASHBOARD_LAYOUT_VERSION, items: draft }
      )

      if ('errors' in response) {
        const message = response.errors.map((error) => `${error.field} ${error.message}`).join(', ')
        Notification.notifyError(message || t('Dashboard::Could not save your dashboard'))
        return
      }

      dispatch(setCurrentUser({ ...currentUser, user_workspace_setting: response }))
      setEditing(false)
      setDraft(null)
      setResetToDefault(false)
    } catch (error) {
      console.error(error)
      Notification.notifyError(t('Dashboard::Could not save your dashboard'))
    } finally {
      setSaving(false)
    }
  }, [draft, saving, resetToDefault, currentUser, dispatch, t])


  const placedTypes = React.useMemo(() => (draft ?? []).map((item) => item.type), [draft])

  return (
    <>
      <Helmet>
        <title>{t('Dashboard::{{__appName}} | Dashboard')}</title>
      </Helmet>
      <TopNavigation
        icon='dashboard-navigation'
        title={t('Dashboard::Dashboard')}
      />
      <ScrollToTopOnMount />

      <PageContent>
        <div className='animated fadeIn'>
          <div className='mb-4 flex justify-end'>
            <DashboardEditActions
              editing={editing}
              saving={saving}
              onCustomise={onCustomise}
              onAutoLayout={onAutoLayout}
              onReset={onReset}
              onCancel={onCancel}
              onSave={onSave}
            />
          </div>
          {isMobile ? (
            <div className={editing ? 'pb-24' : undefined}>
              <DashboardMobileList
                items={items}
                editing={editing}
                onMove={onMoveWidget}
                onRemove={onRemoveWidget}
                widgetProps={widgetProps}
              />
            </div>
          ) : (
            // Same element tree in view and edit mode so widgets don't remount (and refetch / lose
            // internal state) on Customise/Cancel/Save. Bottom padding while editing keeps the last
            // widgets clear of the floating widget picker.
            <div className={editing ? 'pb-24' : undefined}>
              <DashboardGrid
                items={items}
                editing={editing}
                onChange={onLayoutChange}
                onRemove={onRemoveWidget}
                widgetProps={widgetProps}
              />
            </div>
          )}
        </div>
      </PageContent>
      {editing && <AddWidgetList placedTypes={placedTypes} onAdd={onAddWidget} />}
    </>
  )
}

export default Dashboard
